import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";

import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@mui/material";

import { AlertNotification } from "../utils/Notifications";
import useAuth from "../../hooks/useAuth";

import { createDoubt } from "../../functions/server-requests/doubt";

const AskDoubt = ({ openAskDoubt, setOpenAskDoubt }) => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [openAlert, setOpenAlert] = useState(false);
  const [message, setMessage] = useState("");
  const [isSubmitting, setSubmitting] = useState(false);

  const validationSchema = yup.object({
    title: yup
      .string()
      .required("Required")
      .min(4, "Must be at least 4 characters")
      .max(256, "Must be at most 20 or 256 characters"),
    desc: yup
      .string()
      .required("Required")
      .min(8, "Must be at least 8 characters")
      .max(2048, "Must be at most 2048 characters")
  });

  const handleSubmit = async (data) => {
    setSubmitting(true);
    setOpenAlert(false);

    const createDoubtRes = await createDoubt({
      askedById: auth.userId,
      askedBy: auth.name,
      title: data.title.trim(),
      desc: data.desc.trim()
    });

    if (createDoubtRes.status === 201) {
      setOpenAskDoubt(false);
      formik.resetForm();
      navigate(`/doubt/${createDoubtRes.data.doubtId}`);
    }
    else {
      setMessage(createDoubtRes.data.message);
      setOpenAlert(true);
    }

    setSubmitting(false);
  }

  const formik = useFormik({
    initialValues: {
      title: "",
      desc: ""
    },
    validationSchema,
    onSubmit: async (values) => {
      handleSubmit(values);
    }
  });

  return (
    <Dialog
      fullWidth
      open={openAskDoubt}
      onClose={() => setOpenAskDoubt(false)}
    >
      <DialogTitle>
        <span className="title m-title">
          Ask doubt
        </span>
      </DialogTitle>

      <div
        style={{
          margin: "auto",
          width: "calc(100% - 48px)"
        }}
      >
        <AlertNotification
          openAlert={openAlert}
          setOpenAlert={setOpenAlert}
          severity="error"
          styles={{ marginBottom: "0" }}
          message={message}
        />
      </div>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <TextField
            fullWidth
            variant="standard"
            sx={{ mb: "16px" }}
            type="text"
            name="title"
            label="Title"
            value={formik.values.title}
            onChange={formik.handleChange}
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={formik.touched.title && formik.errors.title}
            disabled={isSubmitting}
          />

          <TextField
            fullWidth
            multiline
            minRows={4}
            variant="standard"
            type="text"
            name="desc"
            label="Description"
            value={formik.values.desc}
            onChange={formik.handleChange}
            error={formik.touched.desc && Boolean(formik.errors.desc)}
            helperText={formik.touched.desc && formik.errors.desc}
            disabled={isSubmitting}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenAskDoubt(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            type="submit"
            disabled={
              isSubmitting ||
              (formik.values.title.trim().length < 4 ||
              formik.values.desc.trim().length < 8)
            }
          >
            {!isSubmitting
              ? "Submit"
              : "Submitting ..."
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog >
  );
}

export default AskDoubt;